import { supabase } from '../utils/supabase';

// Vendor messaging/inquiry service using Supabase
export const vendorInquiryService = {
  // Send inquiry to vendor
  async sendInquiry(inquiryData) {
    try {
      // Validate UUID format for vendor_id and user_id
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      
      if (!inquiryData?.receiverId || !uuidRegex?.test(inquiryData?.receiverId)) {
        return {
          success: false,
          error: 'Invalid vendor ID format. Please try again.'
        };
      }

      if (!inquiryData?.senderId || !uuidRegex?.test(inquiryData?.senderId)) {
        return {
          success: false,
          error: 'Invalid user ID format. Please sign in again.'
        };
      }

      // First, verify that the vendor exists
      const { data: vendorExists, error: vendorCheckError } = await supabase?.from('vendor_profiles')?.select('id, business_name')?.eq('id', inquiryData?.receiverId)?.single();

      if (vendorCheckError || !vendorExists) {
        console.error('Vendor not found:', vendorCheckError);
        return {
          success: false,
          error: 'Vendor not found. Please try selecting a different vendor.'
        };
      }

      // Parse guest count safely
      let parsedGuestCount = null;
      if (inquiryData?.metadata?.contactInfo?.guestCount) {
        const guestCountStr = inquiryData?.metadata?.contactInfo?.guestCount;
        const match = guestCountStr?.match(/(\d+)/);
        if (match) {
          parsedGuestCount = parseInt(match?.[1]);
        }
      }

      // Parse budget safely  
      let parsedBudget = null;
      if (inquiryData?.metadata?.contactInfo?.budget) {
        const budgetStr = inquiryData?.metadata?.contactInfo?.budget;
        const cleanStr = budgetStr?.replace(/[\$,]/g, '');
        const match = cleanStr?.match(/(\d+)/);
        if (match) {
          parsedBudget = parseInt(match?.[1]);
        }
      }

      // Parse event date safely
      let parsedEventDate = null;
      if (inquiryData?.metadata?.contactInfo?.eventDate) {
        const eventDateStr = inquiryData?.metadata?.contactInfo?.eventDate;
        // Validate date format (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/?.test(eventDateStr)) {
          parsedEventDate = eventDateStr;
        }
      }

      const { data, error } = await supabase?.from('inquiries')?.insert([
          {
            user_id: inquiryData?.senderId,
            vendor_id: inquiryData?.receiverId,
            message: inquiryData?.content || '',
            event_type: inquiryData?.metadata?.eventType || 'Wedding',
            event_date: parsedEventDate,
            guest_count: parsedGuestCount,
            budget: parsedBudget,
            location: inquiryData?.metadata?.contactInfo?.location || null,
            status: 'pending'
          }
        ])?.select()?.single();

      if (error) {
        console.error('Supabase inquiry error:', error);
        
        // Handle specific error codes
        if (error?.code === '22P02') {
          return { 
            success: false, 
            error: 'Invalid data format. Please check your input and try again.' 
          };
        }
        
        if (error?.code === '23503') {
          return { 
            success: false, 
            error: 'Vendor profile not found. Please try refreshing the page.' 
          };
        }

        if (error?.code === '42501') {
          return { 
            success: false, 
            error: 'Permission denied. Please sign in and try again.' 
          };
        }
        
        return { 
          success: false, 
          error: error?.message || 'Failed to send inquiry' 
        };
      }

      return { 
        success: true, 
        data: { 
          inquiryId: data?.id,
          inquiry: data,
          vendorName: vendorExists?.business_name
        } 
      };
    } catch (error) {
      console.error('Error sending inquiry:', error);
      return { 
        success: false, 
        error: 'Network error. Please check your connection and try again.' 
      };
    }
  },

  // Verify vendor exists
  async verifyVendor(vendorId) {
    try {
      const { data, error } = await supabase?.from('vendor_profiles')?.select('id, business_name, category, city, state, is_verified')?.eq('id', vendorId)?.single();

      if (error || !data) {
        return { success: false, error: 'Vendor not found' };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to verify vendor' };
    }
  },

  // Get user inquiries
  async getUserInquiries(userId) {
    try {
      const { data, error } = await supabase?.from('inquiries')?.select(`
          *,
          vendor_profiles (
            id,
            business_name,
            category,
            phone,
            city,
            state
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user inquiries:', error);
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getUserInquiries:', error);
      return { success: false, error: 'Failed to fetch inquiries' };
    }
  },

  // Get vendor inquiries
  async getVendorInquiries(vendorId) {
    try {
      const { data, error } = await supabase?.from('inquiries')?.select(`
          *,
          user_profiles (
            id,
            full_name,
            email,
            phone
          )
        `)?.eq('vendor_id', vendorId)?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching vendor inquiries:', error);
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getVendorInquiries:', error);
      return { success: false, error: 'Failed to fetch inquiries' };
    }
  },

  // Update inquiry status
  async updateInquiryStatus(inquiryId, status) {
    try {
      const { data, error } = await supabase?.from('inquiries')?.update({ 
          status,
          updated_at: new Date()?.toISOString()
        })?.eq('id', inquiryId)?.select()?.single();

      if (error) {
        console.error('Error updating inquiry status:', error);
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateInquiryStatus:', error);
      return { success: false, error: 'Failed to update inquiry status' };
    }
  }
};

// Legacy chat service compatibility layer
export const chatService = {
  // Start chat (creates inquiry)
  async startChat(userId, vendorId) {
    return {
      success: true,
      data: {
        chatId: `${userId}-${vendorId}`, // Mock chat ID for compatibility
        userId,
        vendorId
      }
    };
  },

  // Send message (creates inquiry)
  async sendMessage(messageData) {
    return await vendorInquiryService?.sendInquiry(messageData);
  },

  // Get chat messages (get inquiries)
  async getChatMessages(chatId) {
    // Parse chat ID to get user and vendor IDs
    const [userId, vendorId] = chatId?.split('-');
    
    try {
      const { data, error } = await supabase?.from('inquiries')?.select('*')?.or(`and(user_id.eq.${userId},vendor_id.eq.${vendorId}),and(user_id.eq.${vendorId},vendor_id.eq.${userId})`)?.order('created_at', { ascending: true });

      if (error) {
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch messages' };
    }
  },

  // Get user chats (get user inquiries)
  async getUserChats(userId) {
    return await vendorInquiryService?.getUserInquiries(userId);
  }
};

export default vendorInquiryService;